import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { DURATION_UNIT_TRANSLATION_MAP } from 'src/features/membershipType/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { useListRoles } from 'src/features/role/api/useListRoles';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockSeparator,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import InputFormField from 'src/registry/components/forms/InputFormField';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import SwitchFormField from 'src/registry/components/forms/SwitchFormField';
import { WizardStepInfo } from 'src/registry/ui/Wizard';

type Props = {
    form: UseFormReturn<MembershipTypeFormSchemaValues>;
};

export default function ValidityStep({ form }: Props) {
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { data: rolesData } = useListRoles({ parkingId });
    const { custom_duration, limit_duration, category } = form.watch();

    return (
        <div className="flex gap-4">
            <div className="flex flex-2 flex-col gap-4">
                <SectionBlock>
                    <SectionBlockTitle className="text-base">
                        {formatMessage(membershipTypeMessages.duration)}
                    </SectionBlockTitle>
                    <SectionBlockBody className="flex-col">
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="limit_duration"
                            label={formatMessage(membershipTypeMessages.limitDuration)}
                            disabled={category === MEMBERSHIP_TEMPLATE_CATEGORY.SUBSCRIPTION}
                            description={formatMessage(membershipTypeMessages.limitDurationDescription)}
                            tooltip={formatMessage(membershipTypeMessages.limitDurationTooltip)}
                        />
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="custom_duration"
                            label={formatMessage(membershipTypeMessages.customDuration)}
                            disabled={!limit_duration}
                            description={formatMessage(membershipTypeMessages.customDurationDescription)}
                            tooltip={formatMessage(membershipTypeMessages.customDurationTooltip)}
                        />
                        <div className="flex w-full gap-2">
                            <div className="flex-1">
                                <InputFormField<MembershipTypeFormSchemaValues>
                                    label={formatMessage(membershipTypeMessages.duration)}
                                    required={Boolean(limit_duration) && !custom_duration}
                                    variant="default"
                                    form={form}
                                    name="duration"
                                    type="number"
                                    disabled={!limit_duration || (Boolean(limit_duration) && Boolean(custom_duration))}
                                    description={formatMessage(membershipTypeMessages.durationDescription)}
                                    tooltip={formatMessage(membershipTypeMessages.durationTooltip)}
                                />
                            </div>
                            <div className="flex-1">
                                <SelectFormField<MembershipTypeFormSchemaValues>
                                    key={`${Number(limit_duration)}.${Number(custom_duration)}`}
                                    form={form}
                                    required={Boolean(limit_duration) && !custom_duration}
                                    name="duration_unit"
                                    variant="default"
                                    label={formatMessage(membershipTypeMessages.durationUnit)}
                                    disabled={!limit_duration || (Boolean(limit_duration) && Boolean(custom_duration))}
                                    options={Object.entries(DURATION_UNIT_TRANSLATION_MAP).map(([key, value]) => ({
                                        value: key,
                                        label: formatMessage(value),
                                    }))}
                                    description={formatMessage(membershipTypeMessages.durationUnitDescription)}
                                />
                            </div>
                        </div>
                    </SectionBlockBody>
                    <SectionBlockSeparator />
                    <SectionBlockTitle className="text-base">
                        {formatMessage(membershipTypeMessages.roles)}
                    </SectionBlockTitle>
                    <SectionBlockBody className="flex-col">
                        <MultiSelectFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            variant="default"
                            name="limit_to_roles"
                            required={false}
                            label={formatMessage(membershipTypeMessages.limitToRoles)}
                            options={rolesData?.results}
                            description={formatMessage(membershipTypeMessages.limitToRolesDescription)}
                            tooltip={formatMessage(membershipTypeMessages.limitToRolesTooltip)}
                        />
                    </SectionBlockBody>
                </SectionBlock>
            </div>
            <div className="flex-1">
                <WizardStepInfo
                    header={formatMessage(membershipTypeMessages.validity)}
                    body={formatMessage(membershipTypeMessages.validityBody)}
                    tip={formatMessage(membershipTypeMessages.validityTip)}
                />
            </div>
        </div>
    );
}
